import { Component } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  faCircleArrowLeft,
  faPaperPlane,
  faPenToSquare,
  faTrash,
  faCirclePlus,
  faCircleArrowRight,
  faSearch,
  faHouse,
  faBuilding,
  faHouseChimney,
} from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { MessageResponse } from '../_models/responses';
import { environment } from '../../environments/environment';
import { Notice, NoticeService } from '../_services';
import { PropertyService } from '../_services/property.service';
import { Property } from '../_models/property';

@Component({
  selector: 'app-manage-notices',
  standalone: false,

  templateUrl: './manage-notices.component.html',
  styleUrl: './manage-notices.component.css'
})
export class ManageNoticesComponent {
  action: string = 'list';
  searchProperty = '';

  faCircleArrowLeft = faCircleArrowLeft;
  faPaperPlane = faPaperPlane;
  faPenToSquare = faPenToSquare;
  faTrash = faTrash;
  faCirclePlus = faCirclePlus;
  faCircleArrowRight = faCircleArrowRight;
  faSearch = faSearch;
  faHouse = faHouse;
  faBuilding = faBuilding;
  faHouseChimney = faHouseChimney;

  loading = false;
  loadingProperties = false;
  fetching = false;
  submitNoticeData = false;

  searchNotice = '';

  notices: Notice[] = [];
  properties: Property[] = [];
  property: any;
  selectedNotice: Notice | null = null;

  addNoticeForm!: FormGroup;
  editNoticeForm!: FormGroup;
  viewNoticeForm!: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _location: Location,
    private formBuilder: FormBuilder,
    private noticeService: NoticeService,
    private propertyService: PropertyService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      this.action = params['action'] || 'list';
      this.initializeForms();
      this.loadPropertyContextForAction();
    });
  }

  get fna() {
    return this.addNoticeForm.controls;
  }

  get fne() {
    return this.editNoticeForm.controls;
  }

  goBack(): void {
    this._location.back();
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  onCreateNotice(): void {
    this.router.navigate(['/manage-notices/create']);
  }

  onCancel(): void {
    this.router.navigate(['/manage-notices/list']);
  }

  onSelectedNotice(notice: Notice): void {
    this.selectedNotice = notice;
    localStorage.setItem('notice', JSON.stringify(notice));

    this.viewNoticeForm.patchValue({
      id: notice.id,
      propertyId: notice.propertyId,
      unit_number: notice.unit_number,
      dateVisible: this.formatDateInput(notice.dateVisible),
      shortDescription: notice.shortDescription,
      longDescription: notice.longDescription,
      propertyName: this.property?.name || '',
    });

    this.router.navigate(['/manage-notices/view']);
  }

  onEditNotice(): void {
    if (!this.selectedNotice) {
      this.toastr.error('No notice selected for editing.', 'Error');
      return;
    }

    this.editNoticeForm.patchValue({
      id: this.selectedNotice.id,
      propertyId: this.selectedNotice.propertyId,
      unit_number: this.selectedNotice.unit_number,
      dateVisible: this.formatDateInput(this.selectedNotice.dateVisible),
      shortDescription: this.selectedNotice.shortDescription,
      longDescription: this.selectedNotice.longDescription,
      propertyName: this.property?.name || '',
    });

    this.router.navigate(['/manage-notices/edit']);
  }

  createNotice(): void {
    this.submitNoticeData = true;
    this.fetching = true;

    if (!this.addNoticeForm.valid) {
      this.fetching = false;
      return;
    }

    const formdata = this.addNoticeForm.value;
    const payload = this.buildPayload(formdata);

    this.noticeService.createNotice(payload).subscribe({
      next: (data: MessageResponse) => {
        this.toastr.success(data.message, 'Success');
        this.fetching = false;
        this.submitNoticeData = false;
        this.loadNoticesByProperty();
        this.router.navigate(['/manage-notices/list']);
      },
      error: (error: HttpErrorResponse) => {
        this.fetching = false;
        this.toastr.error('Failed to create notice.', 'Error');
        if (!environment.production) {
          console.log('Notice creation failed: ', error);
        }
      },
    });
  }

  updateNotice(): void {
    this.submitNoticeData = true;
    this.fetching = true;

    if (!this.editNoticeForm.valid) {
      this.fetching = false;
      return;
    }

    const formdata = this.editNoticeForm.value;
    const noticeId = formdata.id;
    const payload = this.buildPayload(formdata);

    this.noticeService.updateNotice(noticeId, payload).subscribe({
      next: (data: MessageResponse) => {
        this.toastr.success(data.message, 'Success');
        this.fetching = false;
        this.submitNoticeData = false;
        localStorage.setItem('notice', JSON.stringify({ id: noticeId, ...payload }));
        this.loadNoticesByProperty();
        this.router.navigate(['/manage-notices/list']);
      },
      error: (error: HttpErrorResponse) => {
        this.fetching = false;
        this.toastr.error('Failed to update notice.', 'Error');
        if (!environment.production) {
          console.log('Notice update failed: ', error);
        }
      },
    });
  }

  onDeleteNotice(): void {
    const noticeId = this.selectedNotice?.id || this.editNoticeForm?.value?.id || this.viewNoticeForm?.value?.id;
    if (!noticeId) {
      this.toastr.error('No notice selected for deletion.', 'Error');
      return;
    }

    this.fetching = true;
    this.noticeService.deleteNotice(noticeId).subscribe({
      next: (data: MessageResponse) => {
        this.toastr.success(data.message, 'Success');
        this.fetching = false;
        localStorage.removeItem('notice');
        this.selectedNotice = null;
        this.loadNoticesByProperty();
        this.router.navigate(['/manage-notices/list']);
      },
      error: (error: HttpErrorResponse) => {
        this.fetching = false;
        this.toastr.error('Failed to delete notice.', 'Error');
        if (!environment.production) {
          console.log('Notice delete failed: ', error);
        }
      },
    });
  }

  private initializeForms(): void {
    this.addNoticeForm = this.formBuilder.group({
      propertyId: ['', Validators.required],
      unit_number: ['', Validators.required],
      dateVisible: ['', Validators.required],
      shortDescription: ['', Validators.required],
      longDescription: ['', Validators.required],
      propertyName: [''],
    });

    this.editNoticeForm = this.formBuilder.group({
      id: ['', Validators.required],
      propertyId: ['', Validators.required],
      unit_number: ['', Validators.required],
      dateVisible: ['', Validators.required],
      shortDescription: ['', Validators.required],
      longDescription: ['', Validators.required],
      propertyName: [''],
    });

    this.viewNoticeForm = this.formBuilder.group({
      id: [{ value: '', disabled: true }, Validators.required],
      propertyId: [{ value: '', disabled: true }, Validators.required],
      unit_number: [{ value: '', disabled: true }, Validators.required],
      dateVisible: [{ value: '', disabled: true }, Validators.required],
      shortDescription: [{ value: '', disabled: true }, Validators.required],
      longDescription: [{ value: '', disabled: true }, Validators.required],
      propertyName: [{ value: '', disabled: true }],
    });
  }

  private loadPropertyContextForAction(): void {
    this.loadingProperties = true;
    this.propertyService.getMyPropertyAssociations().subscribe({
      next: (associations: any[]) => {
        this.properties = associations.map(assoc => ({
          id: assoc.propertyId,
          name: assoc.propertyDetails.name,
          property_type: assoc.propertyDetails.property_type,
          address: assoc.propertyDetails.address,
          obligationCategoryId: assoc.propertyDetails.obligationCategoryId ?? 0,
          premises: []
        })) || [];

        if (this.properties.length === 1) {
          this.setSelectedProperty(this.properties[0]);
        } else {
          const propertystr = localStorage.getItem('property');
          const currentProperty = propertystr ? JSON.parse(propertystr) : null;
          if (currentProperty?.id) {
            const matched = this.properties.find((p) => p.id === currentProperty.id);
            if (matched) {
              this.setSelectedProperty(matched);
            }
          }
        }

        if (this.properties.length > 1 && this.action === 'list') {
          this.action = 'index';
        }

        this.loadingProperties = false;
        this.loadNoticeStateForAction();
      },
      error: () => {
        this.loadingProperties = false;
        this.toastr.error('Failed to load properties.', 'Error');
      },
    });
  }

  onSelectedProperty(property: Property): void {
    this.setSelectedProperty(property);
    this.action = 'list';
    this.loadNoticeStateForAction();
  }

  onChangeProperty(): void {
    this.action = 'index';
  }

  private setSelectedProperty(property: Property): void {
    this.property = property;
    this.propertyService.setProperty(property);
    this.addNoticeForm.patchValue({
      propertyId: this.property.id,
      propertyName: this.property.name,
    });
  }

  private loadNoticeStateForAction(): void {
    if (this.action === 'index') {
      return;
    }

    if (!this.property?.id) {
      return;
    }

    if (this.action === 'list') {
      this.loadNoticesByProperty();
      return;
    }

    if (this.action === 'create') {
      this.addNoticeForm.patchValue({
        propertyId: this.property?.id || '',
        unit_number: '',
        dateVisible: '',
        shortDescription: '',
        longDescription: '',
        propertyName: this.property?.name || '',
      });
      return;
    }

    const noticestr = localStorage.getItem('notice');
    this.selectedNotice = noticestr ? JSON.parse(noticestr) : null;

    if (!this.selectedNotice?.id) {
      this.toastr.warning('Select a notice from the list first.', 'Info');
      this.router.navigate(['/manage-notices/list']);
      return;
    }

    this.noticeService.getNotice(this.selectedNotice.id).subscribe({
      next: (notice: Notice) => {
        this.selectedNotice = notice;
        localStorage.setItem('notice', JSON.stringify(notice));

        if (this.action === 'view') {
          this.viewNoticeForm.patchValue({
            id: notice.id,
            propertyId: notice.propertyId,
            unit_number: notice.unit_number,
            dateVisible: this.formatDateInput(notice.dateVisible),
            shortDescription: notice.shortDescription,
            longDescription: notice.longDescription,
            propertyName: this.property?.name || '',
          });
        }

        if (this.action === 'edit') {
          this.editNoticeForm.patchValue({
            id: notice.id,
            propertyId: notice.propertyId,
            unit_number: notice.unit_number,
            dateVisible: this.formatDateInput(notice.dateVisible),
            shortDescription: notice.shortDescription,
            longDescription: notice.longDescription,
            propertyName: this.property?.name || '',
          });
        }
      },
      error: (error: HttpErrorResponse) => {
        this.toastr.error('Failed to load selected notice.', 'Error');
        if (!environment.production) {
          console.log('Notice fetch failed: ', error);
        }
        this.router.navigate(['/manage-notices/list']);
      },
    });
  }

  private loadNoticesByProperty(): void {
    if (!this.property?.id) {
      this.notices = [];
      return;
    }

    this.loading = true;
    this.noticeService.getNoticesByPropertyId(this.property.id).subscribe({
      next: (notices: Notice[]) => {
        this.notices = notices || [];
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.toastr.error('Failed to load notices.', 'Error');
        if (!environment.production) {
          console.log('Notice list fetch failed: ', error);
        }
      },
    });
  }

  private buildPayload(formdata: any) {
    return {
      propertyId: Number(formdata.propertyId),
      unit_number: Number(formdata.unit_number),
      dateVisible: formdata.dateVisible,
      shortDescription: formdata.shortDescription,
      longDescription: formdata.longDescription,
    };
  }

  private formatDateInput(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

}
